import { memo, useCallback } from "react";
import { useNodeConnections, type NodeProps } from "@xyflow/react";
import { NodeControl } from "~/components/controls/NodeControl";
import { SocketHandle } from "~/components/SocketHandle";
import type { SpiroNode } from "~/lib/graph";
import { getDefinition } from "~/nodes/registry";
import { useGraphStore } from "~/store/graphStore";

/**
 * Renders any registered node from its definition. Outputs sit at the top right, body
 * controls in the middle, inputs at the bottom left, matching the old layout.
 */
function GraphNodeImpl({ id, type, data, selected }: NodeProps<SpiroNode>) {
  const definition = getDefinition(type);

  const setNodeData = useGraphStore((state) => state.setNodeData);
  const previews = useGraphStore((state) => state.previews[id]);
  const errors = useGraphStore((state) => state.errors[id]);

  // Which target handles currently have an edge — an occupied input hides its control.
  const connections = useNodeConnections({ id, handleType: "target" });
  const occupied = new Set(
    connections.map((connection) => connection.targetHandle),
  );

  const onChange = useCallback(
    (key: string, value: unknown) => setNodeData(id, key, value),
    [id, setNodeData],
  );

  if (!definition) {
    return (
      <div className="rounded-lg border border-rose-500 bg-panel px-3 py-2 text-xs text-rose-300">
        Unknown node type “{type}”
      </div>
    );
  }

  return (
    <div
      style={{ width: definition.width ?? 200 }}
      className={`rounded-lg border bg-panel shadow-lg transition-colors ${
        selected ? "border-teal-400" : "border-neutral-700"
      }`}
    >
      <header className="rounded-t-lg border-b border-neutral-700 bg-panel-raised px-3 py-2 text-xs font-bold tracking-wider text-neutral-100 uppercase">
        {definition.label}
      </header>

      <div className="flex flex-col gap-0.5 px-3 py-2">
        {definition.outputs.map((output) => (
          <div
            key={output.key}
            className="relative flex items-center justify-end py-1"
          >
            <span className="text-xs text-neutral-300">{output.label}</span>
            <SocketHandle id={output.key} kind={output.socket} type="source" />
          </div>
        ))}

        {definition.controls.map((control) => (
          <NodeControl
            key={control.key}
            spec={control}
            value={data[control.key]}
            error={errors?.[control.key]}
            preview={previews?.[control.key]}
            onChange={onChange}
          />
        ))}

        {definition.inputs.map((input) => (
          <div
            key={input.key}
            className="relative flex min-h-8 items-center py-1"
          >
            <SocketHandle id={input.key} kind={input.socket} type="target" />

            <div className="min-w-0 flex-1 pl-1">
              {input.control && !occupied.has(input.key) ? (
                <NodeControl
                  spec={input.control}
                  value={data[input.control.key]}
                  error={errors?.[input.control.key]}
                  preview={previews?.[input.control.key]}
                  onChange={onChange}
                />
              ) : (
                <span className="text-xs text-neutral-300">{input.label}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {errors?.["__node"] ? (
        <p className="rounded-b-lg bg-rose-950/60 px-3 py-1.5 text-[10px] text-rose-300">
          {errors["__node"]}
        </p>
      ) : null}
    </div>
  );
}

export const GraphNode = memo(GraphNodeImpl);
